export type ContentBlock =
  | { type: "text"; content: string }
  | { type: "tip"; content: string }
  | { type: "prompt"; content: string; label?: string }
  | { type: "cta"; href: string; label: string; description?: string }
  | { type: "list"; items: string[] }
  | { type: "comparison"; left: { title: string; items: string[] }; right: { title: string; items: string[] } };

export interface PostSection {
  title: string;
  blocks: ContentBlock[];
}

export interface AcademyPost {
  slug: string;
  title: string;
  description: string;
  category: "oc" | "general";
  categoryLabel: string;
  readTime: string;
  coverImage: string;
  sections: PostSection[];
}

export const ACADEMY_POSTS: AcademyPost[] = [
  // ─── OC FOCUSED POSTS ────────────────────────────────────────────────

  {
    slug: "idea-to-anime-oc",
    title: "From Idea to Anime OC: A Complete AI Workflow",
    description:
      "The full Text → Image → Video pipeline. Go from a rough character idea to a fully animated anime OC using AnimePup's Create and Animate tools.",
    category: "oc",
    categoryLabel: "Original Character",
    readTime: "8 min read",
    coverImage: "/academy/idea-to-anime-oc.png",
    sections: [
      {
        title: "The Three-Step Pipeline",
        blocks: [
          {
            type: "text",
            content:
              "Building an anime original character (OC) with AI isn't a single-shot process — it's a pipeline. The best results come from treating each stage deliberately: first you generate a base design, then you refine it, and finally you bring it to life with animation. AnimePup is built around exactly this workflow.",
          },
          {
            type: "list",
            items: [
              "Step 1: Generate initial designs with Create Character (text → image)",
              "Step 2: Pick your favorite variation and iterate on it",
              "Step 3: Animate the final design with Animate Character (image → video)",
            ],
          },
        ],
      },
      {
        title: "Step 1: Generate Initial Designs",
        blocks: [
          {
            type: "text",
            content:
              "Start broad. Your first prompt doesn't need to be perfect — the goal is to explore the possibility space. Write a prompt that captures the core identity of your character: their archetype, a defining visual feature, and a general mood.",
          },
          {
            type: "prompt",
            label: "Starting prompt",
            content:
              "anime girl with silver twin-tails and golden eyes, wearing a dark blue military coat with gold trim, confident smirk, upper body portrait, soft studio lighting, detailed anime illustration",
          },
          {
            type: "tip",
            content:
              "Generate 3–4 variations from the same prompt. Small differences in generation give you a range of interpretations to choose from. Think of it like concept art — you're exploring before committing.",
          },
          {
            type: "cta",
            href: "/create-character",
            label: "Open Create Character",
            description: "Start generating your OC designs right now",
          },
        ],
      },
      {
        title: "Step 2: Refine Your Favorite",
        blocks: [
          {
            type: "text",
            content:
              "Once you have a variation you like, tweak the prompt to push it further. Add specificity: describe the exact hairstyle, adjust the outfit details, change the expression, or shift the color palette. Each generation gets you closer to the definitive version.",
          },
          {
            type: "prompt",
            label: "Refined prompt",
            content:
              "anime girl with long silver twin-tails past her waist, sharp golden eyes with slight glow, navy blue military overcoat with ornate gold epaulettes and buttons, black gloves, confident smirk, upper body portrait, dramatic rim lighting from behind, highly detailed anime illustration, clean lineart",
          },
          {
            type: "tip",
            content:
              "Keep a \"prompt journal\" — save prompts that produce results you like. You'll build a personal library of keywords and phrasing that work with your aesthetic.",
          },
        ],
      },
      {
        title: "Step 3: Bring It to Life",
        blocks: [
          {
            type: "text",
            content:
              "With your final character image ready, switch to Animate Character. Upload your chosen image and write a motion prompt that describes how the character should move. Be specific about the action, camera, and environment.",
          },
          {
            type: "prompt",
            label: "Animation prompt",
            content:
              "the character turns her head slowly toward the camera, silver twin-tails swaying gently, wind blowing her coat, dramatic backlight, cinematic anime scene",
          },
          {
            type: "text",
            content:
              "The key to good animation prompts is describing motion clearly. What part of the character moves? How fast? What's the camera doing? These details make the difference between a stiff clip and something that feels alive.",
          },
          {
            type: "cta",
            href: "/animate-character",
            label: "Open Animate Character",
            description: "Upload your OC image and animate it",
          },
        ],
      },
      {
        title: "Iteration Is the Secret",
        blocks: [
          {
            type: "text",
            content:
              "The creators who get the best results aren't writing perfect prompts on their first try — they're iterating. Generate, evaluate, adjust, repeat. Each cycle teaches you what the model responds to, and your characters get sharper every time.",
          },
        ],
      },
    ],
  },

  {
    slug: "anime-prompting-101",
    title: "Anime Prompting 101: How to Get EXACT Characters You Want",
    description:
      "Master the anatomy of a strong anime prompt. Learn reference-style keywords for hair, eyes, outfits, and archetypes to generate exactly what you're imagining.",
    category: "oc",
    categoryLabel: "Original Character",
    readTime: "7 min read",
    coverImage: "/academy/anime-prompting-101.png",
    sections: [
      {
        title: "Anatomy of a Strong Prompt",
        blocks: [
          {
            type: "text",
            content:
              "Every effective anime prompt has four layers: Subject, Style, Details, and Composition. Skip any layer and you lose control over the output. Stack them deliberately and you can generate characters that match what's in your head.",
          },
          {
            type: "list",
            items: [
              "Subject — Who is the character? (gender, age range, archetype)",
              "Style — What does the art look like? (modern anime, retro 90s, cinematic, cel-shaded)",
              "Details — What specific visual features define them? (hair, eyes, outfit, accessories)",
              "Composition — How is the image framed? (portrait, full body, action pose, camera angle)",
            ],
          },
        ],
      },
      {
        title: "Reference-Style Keywords: Hair",
        blocks: [
          {
            type: "text",
            content:
              "Hair is the single most defining feature of an anime character. Be explicit about length, style, color, and movement.",
          },
          {
            type: "list",
            items: [
              "Length: short pixie cut, shoulder-length, waist-length, floor-length",
              "Style: twin-tails, single side ponytail, messy bun, straight hime-cut, spiky, braided",
              "Color: platinum blonde, cherry red, deep navy blue, silver-white, gradient pink-to-purple",
              "Motion: windswept, flowing in the breeze, static and neat, wild and untamed",
            ],
          },
          {
            type: "prompt",
            label: "Example",
            content:
              "anime girl with long waist-length cherry red hair in a loose side braid, messy strands framing her face",
          },
        ],
      },
      {
        title: "Reference-Style Keywords: Eyes & Expression",
        blocks: [
          {
            type: "text",
            content:
              "Eyes carry the character's personality. Combine color, shape, and expression for distinct results.",
          },
          {
            type: "list",
            items: [
              "Color: heterochromia (one blue one gold), emerald green, deep crimson, soft lavender",
              "Shape: sharp narrow eyes, large round eyes, half-lidded, upturned cat-eyes",
              "Expression: determined gaze, gentle smile, cold stare, mischievous grin, melancholic look",
            ],
          },
        ],
      },
      {
        title: "Reference-Style Keywords: Outfit & Archetype",
        blocks: [
          {
            type: "text",
            content:
              "Outfits communicate character archetype instantly. Pair clothing descriptors with role keywords for clarity.",
          },
          {
            type: "list",
            items: [
              "School: sailor uniform, blazer and plaid skirt, gakuran, PE uniform",
              "Fantasy: plate armor with cape, dark mage robes, ranger leather gear, shrine maiden hakama",
              "Modern: oversized hoodie, streetwear with bomber jacket, formal suit, idol stage outfit",
              "Archetypes: tsundere, kuudere, genki girl, mysterious loner, battle-hardened warrior",
            ],
          },
          {
            type: "prompt",
            label: "Combining it all",
            content:
              "anime boy, sharp narrow crimson eyes, spiky white hair, wearing a torn black cloak over light armor, battle-hardened warrior archetype, upper body portrait, dramatic side lighting, modern anime illustration style",
          },
          {
            type: "cta",
            href: "/create-character",
            label: "Try These Prompts Now",
            description: "Open Create Character and experiment with reference keywords",
          },
        ],
      },
      {
        title: "Controlling Art Style",
        blocks: [
          {
            type: "text",
            content:
              "Adding explicit style keywords shifts the entire aesthetic. Here are reliable style anchors:",
          },
          {
            type: "list",
            items: [
              "\"modern anime illustration\" — clean lines, vibrant, contemporary look",
              "\"90s retro anime\" — softer colors, visible grain, nostalgic feel",
              "\"cinematic anime\" — dramatic lighting, depth of field, movie-quality framing",
              "\"cel-shaded anime\" — flat colors, bold outlines, classic hand-drawn feel",
              "\"watercolor anime\" — soft washes, painterly edges, dreamy atmosphere",
            ],
          },
          {
            type: "tip",
            content:
              "Put the style keyword near the end of your prompt for the strongest effect. The model tends to weight later descriptors as global style modifiers.",
          },
        ],
      },
    ],
  },

  {
    slug: "control-anime-art-style",
    title: "How to Control Anime Art Style with AI (Shonen vs Slice of Life vs Cinematic)",
    description:
      "Learn the exact prompt keywords that steer AI generation toward specific anime aesthetics — from high-energy shonen action to soft slice-of-life warmth to cinematic drama.",
    category: "oc",
    categoryLabel: "Original Character",
    readTime: "6 min read",
    coverImage: "/academy/control-anime-art-style.png",
    sections: [
      {
        title: "Why Style Direction Matters",
        blocks: [
          {
            type: "text",
            content:
              "The same character can look completely different depending on the art style. A schoolgirl rendered in shonen style has bold outlines and dynamic energy. The same character in slice-of-life style has soft lighting and gentle detail. Style keywords are the lever that controls this — and most people don't use them deliberately enough.",
          },
        ],
      },
      {
        title: "Shonen Style: Bold, Dynamic, Exaggerated",
        blocks: [
          {
            type: "text",
            content:
              "Shonen anime (think Naruto, My Hero Academia, Jujutsu Kaisen) prioritizes energy and impact. Visually, this means exaggerated proportions, strong outlines, intense expressions, and dynamic composition.",
          },
          {
            type: "list",
            items: [
              "\"dynamic action pose, bold lineart, exaggerated proportions\"",
              "\"speed lines, energy aura, intense expression\"",
              "\"dramatic low angle, debris flying, shonen anime style\"",
              "\"vibrant saturated colors, strong shadows, impact frame\"",
            ],
          },
          {
            type: "prompt",
            label: "Shonen example",
            content:
              "anime boy mid-punch with glowing fist, intense determined expression, speed lines radiating outward, bold black lineart, dramatic low angle, debris and sparks, vibrant shonen anime style, high energy",
          },
        ],
      },
      {
        title: "Slice of Life Style: Soft, Warm, Intimate",
        blocks: [
          {
            type: "text",
            content:
              "Slice of life (think Violet Evergarden, Your Name, March Comes In Like a Lion) values subtlety. Soft lighting, natural color palettes, quiet emotion, and atmospheric detail define the look.",
          },
          {
            type: "list",
            items: [
              "\"soft diffused lighting, warm golden hour, gentle atmosphere\"",
              "\"pastel color palette, subtle expression, quiet mood\"",
              "\"detailed background, everyday setting, natural pose\"",
              "\"soft shadows, bokeh, slice of life anime style\"",
            ],
          },
          {
            type: "prompt",
            label: "Slice of life example",
            content:
              "anime girl sitting by a window reading a book, warm afternoon sunlight streaming through curtains, soft shadows on her face, gentle smile, pastel colors, detailed cozy room background, slice of life anime style, peaceful atmosphere",
          },
        ],
      },
      {
        title: "Cinematic Style: Dramatic, Filmic, Atmospheric",
        blocks: [
          {
            type: "text",
            content:
              "Cinematic anime (think Attack on Titan's key frames, Makoto Shinkai films, Demon Slayer's ufotable sequences) looks like it belongs on a movie screen. Strong directional lighting, cinematic aspect ratios, depth of field, and dramatic camera angles define this aesthetic.",
          },
          {
            type: "list",
            items: [
              "\"dramatic rim lighting, volumetric fog, cinematic composition\"",
              "\"shallow depth of field, lens flare, widescreen framing\"",
              "\"high contrast, moody atmosphere, cinematic anime\"",
              "\"dynamic camera angle, sweeping vista, theatrical lighting\"",
            ],
          },
          {
            type: "prompt",
            label: "Cinematic example",
            content:
              "anime warrior standing on a cliff edge overlooking a burning city at sunset, dramatic rim lighting from behind, volumetric smoke and embers, wind whipping their cloak, widescreen cinematic composition, high contrast, cinematic anime style",
          },
          {
            type: "cta",
            href: "/create-character",
            label: "Experiment with Styles",
            description: "Try generating the same character in different styles",
          },
        ],
      },
      {
        title: "Mixing Styles",
        blocks: [
          {
            type: "text",
            content:
              "You're not limited to one style. Some of the most striking results come from blending. Try combining shonen character energy with cinematic lighting, or slice-of-life warmth with dramatic composition. The key is being intentional about which elements you borrow from each style.",
          },
          {
            type: "tip",
            content:
              "Try this experiment: take the exact same character prompt and generate it three times — once with \"shonen anime style\", once with \"slice of life anime style\", and once with \"cinematic anime style\". Comparing the results teaches you exactly how much weight these keywords carry.",
          },
        ],
      },
    ],
  },

  // ─── GENERAL POSTS ────────────────────────────────────────────────────

  {
    slug: "prompt-to-scene",
    title: "From Prompt to Scene: Structuring Anime Video Prompts That Work",
    description:
      "Break your animation prompts into subject, action, camera, environment, and mood. Learn the structure behind prompts that produce great anime video clips.",
    category: "general",
    categoryLabel: "General",
    readTime: "7 min read",
    coverImage: "/academy/prompt-to-scene.png",
    sections: [
      {
        title: "The Five-Part Video Prompt Formula",
        blocks: [
          {
            type: "text",
            content:
              "Image prompts and video prompts are different beasts. In video, you're not just describing a still frame — you're describing motion over time. The most reliable approach is to break every video prompt into five components: Subject, Action, Camera, Environment, and Mood.",
          },
          {
            type: "list",
            items: [
              "Subject — Who or what is in the scene",
              "Action — What moves, how fast, in which direction",
              "Camera — How the virtual camera behaves (pan, zoom, track, static)",
              "Environment — Where the scene takes place, lighting conditions",
              "Mood — The emotional tone (epic, peaceful, tense, melancholic)",
            ],
          },
        ],
      },
      {
        title: "Writing Motion Clearly",
        blocks: [
          {
            type: "text",
            content:
              "The most common mistake in video prompts is vague motion. \"A character fighting\" gives the model almost nothing to work with. Instead, describe the specific movement: what part of the body moves, in what direction, how quickly, and what happens as a result.",
          },
          {
            type: "comparison",
            left: {
              title: "Vague (avoid)",
              items: [
                "character fighting",
                "girl running",
                "boy using magic",
                "character looking around",
              ],
            },
            right: {
              title: "Specific (do this)",
              items: [
                "character throws a right hook, fist glowing with energy, twisting at the hip",
                "girl sprinting forward, hair streaming behind, arms pumping, slight lean forward",
                "boy raising his right hand as blue light spirals upward from his palm",
                "character slowly turns her head from left to right, eyes scanning the horizon",
              ],
            },
          },
        ],
      },
      {
        title: "Camera Language for Video",
        blocks: [
          {
            type: "text",
            content:
              "Camera keywords dramatically change the feel of the animation. A static camera feels observational. A tracking shot feels immersive. A slow zoom builds tension.",
          },
          {
            type: "list",
            items: [
              "\"camera slowly pans left to right\" — establishes a scene, reveals environment",
              "\"camera tracking the character from behind\" — immersive, following the action",
              "\"slow zoom in on character's face\" — builds tension, emotional close-up",
              "\"camera circles around the character\" — dramatic reveal, dynamic energy",
              "\"static camera, character moves through frame\" — observational, lets motion speak",
              "\"camera pulls back to reveal the full scene\" — dramatic reveal of scale",
            ],
          },
          {
            type: "prompt",
            label: "Full structured prompt",
            content:
              "anime swordswoman unsheathes her katana in a fluid draw, blade gleaming, camera slowly zooms in from medium shot to close-up on her determined face, cherry blossom petals drifting through a moonlit temple garden, tense cinematic atmosphere",
          },
          {
            type: "cta",
            href: "/animate-character",
            label: "Try a Structured Prompt",
            description: "Upload an image and test the five-part formula",
          },
        ],
      },
      {
        title: "Environment Sets the Stage",
        blocks: [
          {
            type: "text",
            content:
              "Don't leave the background to chance. Even a few environmental keywords ground the scene and give the model context for lighting and atmosphere.",
          },
          {
            type: "list",
            items: [
              "Settings: rooftop at sunset, dense forest at night, futuristic city street in rain, classroom with afternoon light",
              "Lighting: golden hour, harsh noon sun, neon-lit, moonlight through clouds, candlelight",
              "Weather/particles: gentle rain, heavy snow, cherry blossom petals, floating embers, dust motes",
            ],
          },
          {
            type: "tip",
            content:
              "Particles (petals, rain, embers, snow) add beautiful secondary motion to any scene. They make animations feel more alive even when the main action is subtle.",
          },
        ],
      },
    ],
  },

  {
    slug: "viral-anime-clips",
    title: "How to Create Viral Anime Clips with AI (TikTok / Reels Strategy)",
    description:
      "Optimize your AI anime clips for social media. Learn about grabbing attention in the first 2 seconds, creating loops, and structuring clips for maximum engagement.",
    category: "general",
    categoryLabel: "General",
    readTime: "6 min read",
    coverImage: "/academy/viral-anime-clips.png",
    sections: [
      {
        title: "The First 2 Seconds Decide Everything",
        blocks: [
          {
            type: "text",
            content:
              "On TikTok and Reels, users scroll past content in under two seconds. Your clip needs to open with immediate visual impact — not a slow build. The hook needs to be visual, not narrative. People aren't reading your caption first.",
          },
          {
            type: "list",
            items: [
              "Start mid-action, not before the action begins",
              "Use dramatic lighting or a striking color contrast in the opening frame",
              "Lead with your character's face — faces stop the scroll",
              "Avoid slow fades or gradual reveals as openers",
            ],
          },
          {
            type: "prompt",
            label: "High-hook opening",
            content:
              "close-up of anime girl's eye snapping open, iris glowing bright blue, camera rapidly zooms out to reveal her standing in a storm of energy, hair whipping violently, dramatic lighting, intense anime scene",
          },
        ],
      },
      {
        title: "Loopability: The Secret to Watch Time",
        blocks: [
          {
            type: "text",
            content:
              "The algorithm rewards watch time, and the easiest way to inflate watch time is seamless loops. When a clip ends and restarts without a visible break, viewers often watch 2–3 loops before realizing — and the algorithm counts every second.",
          },
          {
            type: "list",
            items: [
              "Match the ending state to the opening state (same camera position, same mood)",
              "Avoid prompts that end with dramatic finality (explosions, falls)",
              "Continuous motions loop best: walking, wind blowing, particles floating, breathing",
              "Subtle loops outperform dramatic ones — viewers shouldn't notice the restart",
            ],
          },
          {
            type: "tip",
            content:
              "Wind, hair movement, and particle effects (rain, petals, snow) create natural loops because they're continuous. Build your prompt around an action that can cycle smoothly.",
          },
        ],
      },
      {
        title: "Prompt Templates for Social Clips",
        blocks: [
          {
            type: "text",
            content:
              "Here are three proven prompt structures for clips that perform well on short-form platforms:",
          },
          {
            type: "prompt",
            label: "The Character Reveal",
            content:
              "anime character with glowing eyes turns slowly toward the camera, wind blowing their hair, dramatic backlight, sparks and embers drifting, dark moody atmosphere, cinematic anime, slow motion",
          },
          {
            type: "prompt",
            label: "The Atmospheric Loop",
            content:
              "anime girl standing on a rooftop at night, city lights below, hair and skirt gently blowing in the wind, soft rain falling, neon reflections, lo-fi mood, peaceful anime scene",
          },
          {
            type: "prompt",
            label: "The Action Hook",
            content:
              "anime swordsman dashes forward in a burst of speed, afterimage trail, camera tracking from the side, ground cracking beneath each step, intense energy, shonen anime style, dynamic motion",
          },
          {
            type: "cta",
            href: "/animate-character",
            label: "Create Your Clip",
            description: "Generate a social-ready anime clip in minutes",
          },
        ],
      },
      {
        title: "Posting Strategy",
        blocks: [
          {
            type: "text",
            content:
              "Content matters most, but timing and framing help. A few things that consistently boost performance:",
          },
          {
            type: "list",
            items: [
              "Post in 9:16 vertical format (standard for Reels/TikTok)",
              "Add trending audio — the algorithm heavily favors clips with popular sounds",
              "Use text overlay for context (\"I made this with AI\" performs well for curiosity)",
              "Post consistently — 1 clip per day beats 7 clips on Monday",
              "Engage in anime/AI art communities to build initial traction",
            ],
          },
        ],
      },
    ],
  },

  {
    slug: "simulate-sakuga",
    title: "How to Simulate Sakuga Using AI (Prompts for Dynamic Motion & Impact)",
    description:
      "Understand what sakuga is and learn how to prompt for fluid, high-energy animation sequences — expressive motion, camera dynamics, and impactful effects.",
    category: "general",
    categoryLabel: "General",
    readTime: "7 min read",
    coverImage: "/academy/simulate-sakuga.png",
    sections: [
      {
        title: "What Sakuga Actually Is",
        blocks: [
          {
            type: "text",
            content:
              "Sakuga (作画) literally means \"drawing\" in Japanese, but in anime community context it refers to sequences of exceptionally fluid, expressive, high-quality animation — the moments in an episode where a key animator is clearly pouring their skill into every frame. Think the Naruto vs. Pain fight, any ufotable Demon Slayer sequence, or the ballroom scene in Spy x Family.",
          },
          {
            type: "text",
            content:
              "What makes sakuga feel different from regular animation: high frame-rate motion, exaggerated squash-and-stretch, dynamic camera movement, expressive smear frames, and impactful secondary effects (debris, sparks, shockwaves). While AI can't replicate true key-frame animation, you can prompt for many of these qualities.",
          },
        ],
      },
      {
        title: "Prompting for High-Energy Motion",
        blocks: [
          {
            type: "text",
            content:
              "The core of sakuga is motion quality. To push AI generation toward more dynamic, fluid-feeling animation, use strong action verbs and intensity modifiers.",
          },
          {
            type: "comparison",
            left: {
              title: "Low energy (stiff results)",
              items: [
                "character fights",
                "character moves forward",
                "character attacks",
              ],
            },
            right: {
              title: "High energy (fluid results)",
              items: [
                "character delivers a devastating spinning kick, body twisting mid-air with explosive force",
                "character lunges forward in a burst of speed, leaving afterimages",
                "character slashes upward in a sweeping arc, blade trailing light",
              ],
            },
          },
          {
            type: "list",
            items: [
              "Use intensity verbs: lunges, bursts, shatters, explodes, whips, surges",
              "Add speed cues: \"in a flash\", \"blinding speed\", \"rapid succession\", \"slow motion\"",
              "Describe follow-through: what happens AFTER the action (impact, ripple, dust cloud)",
            ],
          },
        ],
      },
      {
        title: "Camera Movement for Impact",
        blocks: [
          {
            type: "text",
            content:
              "Sakuga scenes rarely use static cameras. The camera itself is part of the choreography — it shakes on impact, tracks with the action, and zooms in for dramatic beats.",
          },
          {
            type: "list",
            items: [
              "\"camera shakes on impact\" — sells the weight of a hit",
              "\"rapid zoom in on face\" — dramatic reaction shot",
              "\"camera rotates around character mid-action\" — dynamic energy",
              "\"tracking shot following the character at speed\" — immersive chase feeling",
              "\"camera tilts up from ground level\" — emphasizes power and scale",
            ],
          },
          {
            type: "prompt",
            label: "Sakuga-style prompt",
            content:
              "anime warrior leaps into the air and delivers a devastating downward slash, camera rotates around them mid-swing, blade trailing bright energy, the ground shatters on impact with a shockwave of debris and light, dramatic camera shake, intense sakuga-quality animation, cinematic anime",
          },
        ],
      },
      {
        title: "Effects That Sell Impact",
        blocks: [
          {
            type: "text",
            content:
              "Secondary effects are what make sakuga moments feel powerful. Without them, even a fast action feels weightless. Always add at least 2–3 effect layers to your prompt.",
          },
          {
            type: "list",
            items: [
              "Impact: sparks, debris, shockwave, ground cracking, dust cloud",
              "Energy: glowing aura, light trails, energy particles, electrical arcs",
              "Atmosphere: wind gusts, screen shake, motion blur, speed lines",
              "Lighting: flash of light on impact, dramatic rim light, volumetric glow",
            ],
          },
          {
            type: "tip",
            content:
              "Layer your effects. Don't just say \"sparks\" — say \"sparks and debris explode outward from the point of impact, dust cloud billowing, ground cracking in a radial pattern.\" More detail gives the model more to work with.",
          },
          {
            type: "cta",
            href: "/animate-character",
            label: "Create a Sakuga Clip",
            description: "Upload a character and try these high-energy prompts",
          },
        ],
      },
    ],
  },

  {
    slug: "480p-vs-720p",
    title: "480p vs 720p AI Anime: Is Premium Worth It?",
    description:
      "A practical comparison of AnimePup's Standard (480p) and HD (720p) video tiers. When to save gizmos and when HD makes a real difference.",
    category: "general",
    categoryLabel: "General",
    readTime: "5 min read",
    coverImage: "/academy/480p-vs-720p.png",
    sections: [
      {
        title: "The Two Tiers",
        blocks: [
          {
            type: "text",
            content:
              "AnimePup offers two resolution tiers for video generation: Standard (480p) and HD (720p). They use different amounts of gizmos, and the visual difference matters more in some use cases than others. Here's an honest breakdown.",
          },
          {
            type: "comparison",
            left: {
              title: "Standard (480p)",
              items: [
                "Lower gizmo cost per generation",
                "Faster generation time",
                "Good for drafting and iteration",
                "Perfectly fine for social media (most platforms compress anyway)",
                "Ideal for testing prompts before committing to HD",
              ],
            },
            right: {
              title: "HD (720p)",
              items: [
                "Higher gizmo cost per generation",
                "Slightly longer generation time",
                "Noticeably sharper detail, especially on faces and fine lineart",
                "Better for showcasing on larger screens or portfolios",
                "Worth it for final versions of clips you care about",
              ],
            },
          },
        ],
      },
      {
        title: "When 480p Is the Smart Choice",
        blocks: [
          {
            type: "text",
            content:
              "Most of your generations should be at 480p. Here's why: the iteration phase is where you burn the most gizmos, and that's where 480p saves you the most. Prompt experimentation, testing different motions, finding the right camera angle — all of this can happen at standard resolution.",
          },
          {
            type: "list",
            items: [
              "Testing a new prompt or prompt style",
              "Iterating on motion direction or camera angle",
              "Generating multiple variations to choose from",
              "Creating content for TikTok/Reels (compressed to low res anyway)",
              "Exploring a new character design's animation potential",
            ],
          },
          {
            type: "tip",
            content:
              "Use the 480p → 720p workflow: draft at Standard, find the prompt that works, then re-run it in HD for the final version. This typically saves 40–60% of your gizmos versus generating everything in HD.",
          },
        ],
      },
      {
        title: "When HD Makes a Real Difference",
        blocks: [
          {
            type: "text",
            content:
              "There are specific scenarios where the jump to 720p is genuinely worth the extra gizmos:",
          },
          {
            type: "list",
            items: [
              "Close-up face shots — fine details like eyes and hair strands are noticeably crisper",
              "Complex outfits or armor — intricate details hold together better at higher resolution",
              "Slow, subtle animations — when there's less motion, the eye focuses more on image quality",
              "Portfolio or showcase pieces — your best work deserves the best resolution",
              "Scenes with text or symbols — legibility improves significantly at 720p",
            ],
          },
        ],
      },
      {
        title: "The Practical Workflow",
        blocks: [
          {
            type: "text",
            content:
              "Here's the gizmo-efficient workflow most experienced creators follow:",
          },
          {
            type: "list",
            items: [
              "1. Generate your character image at Create Character (costs the same for both tiers)",
              "2. Test 2–3 animation prompts at 480p to find what works",
              "3. Once you nail the prompt, re-run it at 720p for the final version",
              "4. For social media only? You might skip step 3 entirely — 480p looks great on phones",
            ],
          },
          {
            type: "cta",
            href: "/create-character",
            label: "Start Creating",
            description: "Begin with a character design, then animate at your chosen resolution",
          },
        ],
      },
    ],
  },
];

export const OC_POSTS = ACADEMY_POSTS.filter((p) => p.category === "oc");
export const GENERAL_POSTS = ACADEMY_POSTS.filter((p) => p.category === "general");
